import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Select,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import {
  useCreateSupplier,
  E164_REGEX,
  EMAIL_REGEX,
  NAME_MAX,
  CONTACT_PERSON_MAX,
  ADDRESS_MAX,
  CITY_MAX,
  STATE_MAX,
  NOTES_MAX,
  PAYMENT_TERMS_OPTIONS,
  SUPPLIER_COUNTRY_OPTIONS,
  useSupplierCountry,
} from '@/features/suppliers';
import type { CreateSupplierPayload } from '@/features/suppliers';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { clampPostalInput, isValidPostalCode } from '@/utils/address-fields';
import { PURCHASE_CURRENCY_OPTIONS } from '@/utils/purchase-currency';

// ─── Icons ──────────────────────────────────────────────────────────────────

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function clip(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

// ─── Content ────────────────────────────────────────────────────────────────

function SupplierCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateSupplier();
  // The supplier's own country, the tenant's by default (client issue I-3).
  // Its tax id (GSTIN / TRN / VATIN, a generic "Tax ID" outside India and the
  // GCC, none for Qatar/Kuwait), address fields and dial code follow it.
  const tenantCountry = useTenantCountry();
  const [countryCode, setCountryCode] = useState(tenantCountry.iso);
  const country = useSupplierCountry(countryCode);
  const { addressFields, taxId } = country;
  // The currency it invoices in (I-3 part b): follows the country until picked.
  const [currencyChoice, setCurrencyChoice] = useState<string | null>(null);
  const currencyCode = currencyChoice ?? country.suggestedCurrency;

  // Form state
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [gstin, setGstin] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('NET_30');
  const [notes, setNotes] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const errs: Record<string, string> = {};

    if (!name.trim()) errs.name = 'Supplier name is required';
    else if (name.length > NAME_MAX) errs.name = `Max ${NAME_MAX} characters`;

    if (contactPerson.length > CONTACT_PERSON_MAX) {
      errs.contact_person = `Max ${CONTACT_PERSON_MAX} characters`;
    }

    if (phone && !E164_REGEX.test(phone)) {
      errs.phone = 'Must be a valid phone number';
    }

    if (email && !EMAIL_REGEX.test(email)) {
      errs.email = 'Must be a valid email address';
    }

    if (gstin && taxId && !taxId.regex.test(gstin.toUpperCase())) {
      errs.gstin = `Must be a valid ${taxId.label}`;
    }

    if (!isValidPostalCode(pincode, country.iso)) {
      errs.pincode = `${addressFields.postalLabel} is not valid for ${country.name}`;
    }

    if (notes.length > NOTES_MAX) errs.notes = `Max ${NOTES_MAX} characters`;

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const payload: CreateSupplierPayload = {
      name: name.trim(),
      ...(contactPerson && { contact_person: contactPerson.trim() }),
      ...(phone && { phone }),
      ...(email && { email: email.trim() }),
      country_code: country.iso,
      currency_code: currencyCode,
      // Kuwait and Qatar have no supplier tax id: never send one typed for another country.
      ...(gstin && taxId && { gstin: gstin.toUpperCase().trim() }),
      ...(addressLine1 && { address_line1: addressLine1.trim() }),
      ...(addressLine2 && { address_line2: addressLine2.trim() }),
      ...(city && { city: city.trim() }),
      ...(state && { state }),
      ...(pincode && { pincode: pincode.trim() }),
      payment_terms: paymentTerms as CreateSupplierPayload['payment_terms'],
      ...(notes && { notes: notes.trim() }),
    };

    createMutation.mutate(payload, {
      onSuccess: (result) => {
        toast({ title: `Supplier "${result.name}" created`, variant: 'success' });
        navigate(`/inventory/suppliers/${result.id}`);
      },
      onError: (err: Error) => {
        toast({ title: err.message, variant: 'error' });
      },
    });
  }

  // Escape leaves the form; ask first once anything has been typed.
  const formDirty = [name, contactPerson, phone, email, gstin, addressLine1, addressLine2, city, state, pincode, notes]
    .some((v) => v.trim() !== '');
  const { confirmLeave, dialog: leaveDialog } = useUnsavedChangesGuard(formDirty);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // A dropdown that was open handled this Escape (closed itself).
      if (e.key === 'Escape' && e.defaultPrevented) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        confirmLeave(() => navigate('/inventory/suppliers'));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [name, contactPerson, phone, email, countryCode, gstin, addressLine1, addressLine2, city, state, pincode, paymentTerms, notes, confirmLeave]);

  // A new country brings its own region list and tax id rule: the region picked
  // for the old one no longer applies, and stale errors go with it.
  function changeCountry(iso: string) {
    if (iso === countryCode) return;
    setCountryCode(iso);
    setState('');
    setErrors((e) => ({ ...e, gstin: '', pincode: '' }));
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      {/* Compact header — Create is intentionally lighter than List/Detail */}
      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/inventory/suppliers')}
          className="mt-0.5 p-2 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
          aria-label="Back to suppliers"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="border-l-4 border-teal-500 pl-4">
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">New Supplier</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Add a vendor to your directory</p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md p-6 space-y-8">
        {/* Basic Info */}
        <section>
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-gradient-to-b from-teal-500 to-emerald-500" />
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Supplier Name <span className="text-danger-500">*</span>
              </label>
              <Input
                value={name}
                onChange={(v) => { setName(clip(v, NAME_MAX)); if (errors.name) setErrors((e) => ({ ...e, name: '' })); }}
                placeholder="e.g. Fresh Farms Pvt Ltd"
              />
              {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Contact Person
              </label>
              <Input
                value={contactPerson}
                onChange={(v) => { setContactPerson(clip(v, CONTACT_PERSON_MAX)); if (errors.contact_person) setErrors((e) => ({ ...e, contact_person: '' })); }}
                placeholder="e.g. Rajesh Kumar"
              />
              {errors.contact_person && <p className="text-xs text-danger-500 mt-1">{errors.contact_person}</p>}
            </div>
            <div>
              <Select
                variant="searchable"
                label="Country of registration"
                options={SUPPLIER_COUNTRY_OPTIONS}
                value={countryCode}
                onChange={(v) => { if (typeof v === 'string' && v) changeCountry(v); }}
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Sets the tax ID format, address fields and phone code.
              </p>
            </div>
            <div>
              <Select
                variant="searchable"
                label="Currency"
                options={PURCHASE_CURRENCY_OPTIONS}
                value={currencyCode}
                onChange={(v) => { if (typeof v === 'string' && v) setCurrencyChoice(v); }}
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                New purchase orders for this supplier start in this currency.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-gradient-to-b from-brand-500 to-indigo-500" />
            Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Phone
              </label>
              <Input
                variant="phone"
                defaultDial={country.dial}
                value={phone}
                onChange={(v) => { setPhone(v); if (errors.phone) setErrors((e) => ({ ...e, phone: '' })); }}
                placeholder="98765 43210"
              />
              {errors.phone && <p className="text-xs text-danger-500 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(v) => { setEmail(v); if (errors.email) setErrors((e) => ({ ...e, email: '' })); }}
                placeholder="orders@supplier.com"
                inputMode="email"
              />
              {errors.email && <p className="text-xs text-danger-500 mt-1">{errors.email}</p>}
            </div>
          </div>
        </section>

        {/* Tax & Payment */}
        <section>
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-gradient-to-b from-warning-500 to-danger-500" />
            Tax & Payment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Statutory tax id of the supplier's country — `GSTIN` (India) /
                `TRN`·`VATIN`·… (GCC) / "Tax ID" elsewhere. Qatar/Kuwait have
                no statutory id ⇒ field hidden. */}
            {taxId && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {taxId.label}
                </label>
                <Input
                  value={gstin}
                  onChange={(v) => { setGstin(v.toUpperCase().slice(0, taxId.maxLength)); if (errors.gstin) setErrors((e) => ({ ...e, gstin: '' })); }}
                  placeholder={taxId.placeholder}
                  inputClassName="font-mono tracking-wide"
                />
                {errors.gstin && <p className="text-xs text-danger-500 mt-1">{errors.gstin}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Payment Terms
              </label>
              <Select
                options={[...PAYMENT_TERMS_OPTIONS]}
                value={paymentTerms}
                onChange={(v) => setPaymentTerms(typeof v === 'string' ? v : 'NET_30')}
              />
            </div>
          </div>
        </section>

        {/* Address */}
        <section>
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-gradient-to-b from-violet-500 to-pink-500" />
            Address
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Address Line 1
              </label>
              <Input
                value={addressLine1}
                onChange={(v) => setAddressLine1(clip(v, ADDRESS_MAX))}
                placeholder="Street address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Address Line 2
              </label>
              <Input
                value={addressLine2}
                onChange={(v) => setAddressLine2(clip(v, ADDRESS_MAX))}
                placeholder="Landmark, area"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  City
                </label>
                <Input
                  value={city}
                  onChange={(v) => setCity(clip(v, CITY_MAX))}
                  placeholder={addressFields.cityPlaceholder}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {addressFields.regionLabel}
                </label>
                {/* A country with no region list (outside India and the GCC) gets free text. */}
                {addressFields.regionOptions.length > 0 ? (
                  <Select
                    options={addressFields.regionOptions}
                    value={state}
                    onChange={(v) => setState(typeof v === 'string' ? v : '')}
                    placeholder={addressFields.regionPlaceholder}
                  />
                ) : (
                  <Input
                    value={state}
                    onChange={(v) => setState(clip(v, STATE_MAX))}
                    placeholder={addressFields.regionLabel}
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {addressFields.postalLabel}
                </label>
                <Input
                  value={pincode}
                  onChange={(v) => { setPincode(clampPostalInput(v, addressFields)); if (errors.pincode) setErrors((e) => ({ ...e, pincode: '' })); }}
                  placeholder={addressFields.postalPlaceholder}
                  inputMode={addressFields.postalIsFreeForm ? undefined : 'numeric'}
                />
                {errors.pincode && <p className="text-xs text-danger-500 mt-1">{errors.pincode}</p>}
              </div>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section>
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <span className="w-1.5 h-4 rounded-full bg-gradient-to-b from-neutral-400 to-neutral-600" />
            Notes
          </h2>
          <textarea
            className="w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-colors"
            rows={3}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); if (errors.notes) setErrors((er) => ({ ...er, notes: '' })); }}
            placeholder="Internal notes about this supplier..."
            maxLength={NOTES_MAX}
          />
          {errors.notes && <p className="text-xs text-danger-500 mt-1">{errors.notes}</p>}
          <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums">
            {notes.length}/{NOTES_MAX}
          </p>
        </section>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
          <Button variant="secondary" onClick={() => navigate('/inventory/suppliers')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Saving...' : 'Save Supplier'}
          </Button>
        </div>
      </div>
      {leaveDialog}
    </div>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function SupplierCreatePage() {
  return (
    <PageContainer
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Suppliers', href: '/inventory/suppliers' },
        { label: 'New Supplier' },
      ]}
    >
      <ErrorBoundary>
        <SupplierCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
